// Goblin Wood — investigation board: draws the red string between pins
// (recalculated on resize and once images have loaded, since layout
// shifts as photos come in) and wires the mobile swipe arrows. Shared by
// sections/main-collection-banner.liquid (hero) and
// sections/franchise-collection-banner.liquid (homepage section) — every
// element with class .ib-board on the page gets both behaviors, so this
// file only needs to be loaded once per page even if both sections are
// present.
(function () {
  var STRING_COLOR = '#c1121f';
  var MOBILE_BREAKPOINT = 749;

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  function stringSegment(a, b, width, opacity) {
    var mx = (a.x + b.x) / 2;
    var my = (a.y + b.y) / 2 + 16; // gentle natural sag, not a dead-straight line
    var d = 'M' + a.x + ',' + a.y + ' Q' + mx + ',' + my + ' ' + b.x + ',' + b.y;
    // Three stacked strokes read as real twisted string rather than a flat
    // line: a soft dark shadow underneath so it stands out from the cork,
    // the main bright-red stroke, and a thin dashed lighter stroke on top
    // whose dashes fake the twist of individual fibres (an actual raster
    // texture can't practically follow an arbitrary curved SVG path
    // without a library this theme doesn't have, so this is the "fake the
    // twist with a stroke pattern" fallback the brief allows for).
    var op = opacity == null ? 1 : opacity;
    return (
      '<g opacity="' + op + '">' +
      '<path d="' + d + '" fill="none" stroke="rgba(20,10,8,0.4)" stroke-width="' + (width + 1.5) + '" stroke-linecap="round"/>' +
      '<path d="' + d + '" fill="none" stroke="' + STRING_COLOR + '" stroke-width="' + width + '" stroke-linecap="round"/>' +
      '<path d="' + d + '" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="' + (width * 0.3) + '" stroke-linecap="round" stroke-dasharray="3,4"/>' +
      '</g>'
    );
  }

  // Desktop-only: the string doesn't exist below the mobile breakpoint at
  // all (no markup content, no computation, no listeners for it — see
  // initBoard below), so this never runs on mobile in the first place.
  // Kept as a guard too, since a resize can cross the breakpoint between
  // this being scheduled and running.
  function drawStrings(board) {
    var svg = board.querySelector('.ib-string-svg');
    if (!svg) return;
    if (isMobile()) {
      svg.innerHTML = '';
      return;
    }
    var pins = Array.prototype.slice.call(board.querySelectorAll('.ib-pin'));
    if (pins.length < 2) {
      svg.innerHTML = '';
      return;
    }
    var boardRect = board.getBoundingClientRect();
    var points = pins.map(function (pin) {
      var r = pin.getBoundingClientRect();
      return {
        x: r.left + r.width / 2 - boardRect.left,
        y: r.top + r.height * 0.45 - boardRect.top,
      };
    });
    svg.setAttribute('viewBox', '0 0 ' + boardRect.width + ' ' + boardRect.height);

    var width = 3;
    var html = '';
    // Fan out from the index-card pin (points[0]) to every polaroid pin,
    // like a real investigation board...
    for (var i = 1; i < points.length; i++) {
      html += stringSegment(points[0], points[i], width);
    }
    // ...plus a line pin-to-pin across the row itself.
    for (var j = 1; j < points.length - 1; j++) {
      html += stringSegment(points[j], points[j + 1], width);
    }
    svg.innerHTML = html;
  }

  function initBoard(board) {
    var track = board.querySelector('.ib-row');
    var rafPending = false;
    var scrollListenersAttached = false;
    function redraw() {
      drawStrings(board);
    }
    function scheduleRedraw() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        redraw();
      });
    }
    // The string doesn't exist on mobile at all, so its scroll listener
    // is only attached above the breakpoint — attached/detached as the
    // viewport crosses it on resize, not left running (a no-op or not)
    // underneath a media query the whole time.
    function syncScrollListener() {
      if (!track) return;
      if (isMobile()) {
        if (scrollListenersAttached) {
          track.removeEventListener('scroll', scheduleRedraw);
          track.removeEventListener('scrollend', redraw);
          scrollListenersAttached = false;
        }
      } else if (!scrollListenersAttached) {
        track.addEventListener('scroll', scheduleRedraw, { passive: true });
        track.addEventListener('scrollend', redraw, { passive: true });
        scrollListenersAttached = true;
      }
    }
    var imgs = Array.prototype.slice.call(board.querySelectorAll('img'));
    var pending = imgs.length;
    imgs.forEach(function (img) {
      if (img.complete) {
        pending--;
      } else {
        img.addEventListener('load', function () {
          pending--;
          if (pending <= 0) redraw();
        });
        img.addEventListener('error', function () {
          pending--;
          if (pending <= 0) redraw();
        });
      }
    });
    syncScrollListener();
    redraw();
    // Safety re-draws for any layout shift the load listeners miss
    // (webfonts, deferred styles, etc.) — cheap no-op if nothing moved.
    setTimeout(redraw, 350);
    setTimeout(redraw, 1200);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        // Order matters when crossing the breakpoint: attach/detach the
        // scroll listener for the new viewport first, then redraw — a
        // resize from mobile to desktop needs the listener attached
        // before anything scrolls again, and one from desktop to mobile
        // needs redraw() to clear any leftover string immediately.
        syncScrollListener();
        redraw();
      }, 150);
    });

    // The hover "swing" (see CSS @keyframes ib-swing) rotates each
    // .ib-item around transform-origin: top center, which sits right at
    // the pin — the pin's on-screen position barely moves during the
    // swing, so the string stays visually attached without per-frame
    // recalculation. This redraw is just a safety net for the split
    // second after the animation ends, in case of any sub-pixel drift.
    board.addEventListener('animationend', function (e) {
      if (e.animationName === 'ib-swing') redraw();
    });
  }

  function initArrows(board) {
    var track = board.querySelector('.ib-row');
    var arrowsWrap = board.querySelector('[data-ib-arrows]');
    if (!track || !arrowsWrap) return;
    var prev = arrowsWrap.querySelector('.ib-arrow--prev');
    var next = arrowsWrap.querySelector('.ib-arrow--next');

    function cardStep() {
      var item = track.querySelector('.ib-item');
      return item ? item.getBoundingClientRect().width + 24 : 200;
    }
    function updateArrows() {
      // Small tolerance, not <= 0 exactly — a few px of residual scrollLeft
      // at rest (from track padding / scroll-snap rounding) shouldn't read
      // as "not at the start" and leave prev wrongly enabled.
      var atStart = track.scrollLeft <= 10;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 10;
      if (prev) prev.classList.toggle('is-disabled', atStart);
      if (next) next.classList.toggle('is-disabled', atEnd);
    }
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -cardStep(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: cardStep(), behavior: 'smooth' }); });
    track.addEventListener('scroll', updateArrows, { passive: true });
    updateArrows();
    window.addEventListener('resize', updateArrows);
  }

  function initQuickAdd(board) {
    // gw-quick-add.liquid ships with no click-wiring of its own — every
    // caller must provide it (see main-collection-type-carousel.liquid,
    // the reference implementation). The wired-guard is shared with that
    // script's own copy of this same loop: on the collection hero page,
    // both scripts see the SAME .gw-quick-add buttons (that script's
    // query is page-wide, not scoped to its own section), so without a
    // shared guard a click would fire window.gwInterceptAddToCart twice.
    board.querySelectorAll('.gw-quick-add').forEach(function (btn) {
      if (btn.classList.contains('gw-quick-add--soldout')) return;
      if (btn.dataset.gwQuickAddWired) return;
      btn.dataset.gwQuickAddWired = '1';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var variantId = parseInt(this.dataset.variantId, 10);
        if (!variantId) return;
        window.gwInterceptAddToCart(variantId, 1);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.ib-board').forEach(function (board) {
      initBoard(board);
      initArrows(board);
      initQuickAdd(board);
    });
  });
})();
